<template>
    <section class="freelance-search">
        <div class="search-card">
            <form class="search-form" data-testid="search-block-form" @submit="onSubmit">
                <div class="inputs">
                    <div class="category">
                        <input
                            v-model="query"
                            class="category-input"
                            name="category-input"
                            type="text"
                            :maxlength="SEARCH_QUERY_MAX_LENGTH"
                            data-testid="search-block-query-field"
                            :aria-label="categoryLabel || 'category'"
                            :label="categoryLabel"
                            :placeholder="categoryLabel || 'category'"
                            autocomplete="off"
                            list="query-list"
                            debounce="500"
                            @input="onQueryInputChange"
                            @focus="() => (isQuerySearchActive = true)"
                            @blur="onQueryBlur"
                        />
                        <button
                            :class="{'clear-button': true, visible: query?.length > 0}"
                            type="button"
                            data-testid="search-block-query-clear"
                            aria-label="clear-query"
                            @click="clearQuery"
                        >
                            <VJoyIcon name="cross" />
                        </button>
                        <span v-if="categoryIsInError" class="category-error">{{ missingCategoryErrorText }}</span>
                        <div
                            :class="{
                                suggestions: true,
                                visible: isQuerySearchActive && querySuggestions.length > 0,
                            }"
                            data-testid="search-block-query-suggestions"
                        >
                            <ul>
                                <li
                                    v-for="querySuggestion in querySuggestions"
                                    :key="querySuggestionOutput(querySuggestion, 'key')"
                                    @mousedown="(event) => onQuerySuggestionClick(event, querySuggestion)"
                                >
                                    {{ querySuggestion }}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <MaltLocationAutocomplete
                        v-model="location"
                        remote-allowed
                        class="location"
                        :label="locationPlaceholder"
                        input-test-id="search-block-location-field"
                        append-to-self
                        prevent-option-selection
                        :layer="[
                            {name: 'LOCALITY', size: 5},
                            {name: 'COUNTRY', size: 2},
                        ]"
                    />
                </div>
                <div class="links">
                    <VJoyButton type="submit" class="search-form__submit" data-testid="search-block-btn-submit">
                        {{ searchLinkText }}
                    </VJoyButton>
                    <div v-if="aiSearchLinkText" class="ai-search-cta">
                        <span v-if="betweenCtasText" class="between-ctas">{{ betweenCtasText }}</span>
                        <VJoyButton
                            :href="aiSearchLink"
                            rel="nofollow"
                            icon="sparkle"
                            icon-position="right"
                            variant="secondary-ai"
                            type="button"
                            data-testid="search-block-btn-ai-search"
                        >
                            {{ aiSearchLinkText }}
                        </VJoyButton>
                    </div>
                    <div v-if="submitProjectLink && (!user || !user.connected || isCompany)" class="submit-project">
                        <span class="cta-separator" />
                        <a :href="submitProjectLinkWithParams">{{ submitProjectLinkText }}</a>
                    </div>
                </div>
            </form>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {useLocationFromRoute} from '@navbar/common/composables';
    import {navbarEventBus} from '@navbar/events';
    import {useSearchRouteDefinitions} from '@navbar/features/search';
    import {useSearchHelpers, useTypeGuards} from '@navbar-unlogged/composables';
    import {useRouteSearchQueryParam} from '@navbar-unlogged/composables/useRouteSearchQueryParam';
    import {useUserStore} from '@navbar-unlogged/store/user.store';
    import type {QuerySuggestion} from '@navbar-unlogged/types/custom';
    import type {KeyTextField} from '@prismicio/types';
    import {storeToRefs} from 'pinia';
    import {MaltLocationAutocomplete} from '#components';
    import {useTranslation, useGlobalMatching, type Layer, type LayerRequest} from '#imports';
    import type {Location} from '@malt/nuxt-common-components-module/types';
    import {VJoyButton, VJoyIcon} from '@maltjoy/core-vue';

    const SEARCH_QUERY_MAX_LENGTH = 128;

    type Props = {
        categoryLabel: KeyTextField;
        locationLabel: KeyTextField;
        searchLinkText: KeyTextField;
        searchLink: KeyTextField;
        submitProjectLinkText: KeyTextField;
        submitProjectLink: KeyTextField;
        aiSearchLinkText: KeyTextField;
        aiSearchLink: KeyTextField;
        betweenCtasText: KeyTextField;
        missingCategoryErrorText: KeyTextField;
        heapPrefix: string;
        remoteInCountryLabel: KeyTextField;
        briefOrigin?: string;
    };

    const props = withDefaults(defineProps<Props>(), {
        submitProjectLinkText: '',
        submitProjectLink: '',
        aiSearchLinkText: '',
        aiSearchLink: '',
        betweenCtasText: '',
        missingCategoryErrorText: '',
        heapPrefix: '',
        remoteInCountryLabel: '',
        briefOrigin: '',
    });

    const {user, isCompany} = storeToRefs(useUserStore());

    const {isHTMLInputElement, isFilled} = useTypeGuards();
    const {getQueryAutocomplete, getSearchURL} = useSearchHelpers();

    const {currentPage} = useSearchRouteDefinitions();

    const {t} = useTranslation();
    const {isBrowsingGlobalMatching} = useGlobalMatching();

    const searchQueryParams = useRouteSearchQueryParam();

    const query = ref(searchQueryParams.q || '');
    const location = ref<Location | null>(useLocationFromRoute() as Location | null);
    const submitProjectLinkWithParams = ref<string>(computeSubmitProjectLinkWithParams());
    const categoryIsInError = ref(false);
    const querySuggestions = ref<string[]>([]);
    const isQuerySearchActive = ref(false);

    const locationPlaceholder = computed(() => (isBrowsingGlobalMatching.value ? t('search.location.com.label') : props.locationLabel || 'location'));

    function querySuggestionOutput(querySuggestion: QuerySuggestion | string, field: 'key' | 'text'): string {
        return typeof querySuggestion === 'string' ? querySuggestion : querySuggestion[field];
    }

    function onSubmit(e: Event) {
        e.preventDefault();

        if (!isFilled(query.value)) {
            categoryIsInError.value = true;
            return;
        } else {
            categoryIsInError.value = false;
        }

        if (currentPage.value?.name === 'Search') {
            navbarEventBus.emit('navbar-search-requested', {
                query: query.value,
                location: location.value,
                sourceComponent: 'navbar_search',
            });
        } else {
            window.location.href = getSearchURL({
                baseURLLink: props.searchLink,
                query: query.value,
                location: location.value,
                sourceComponent: 'navbar_search_expanded',
            });
        }
    }

    async function onQueryInputChange(event: Event) {
        if (!isHTMLInputElement(event.target)) {
            return;
        }

        query.value = event.target.value ?? '';

        if (props.submitProjectLink) {
            submitProjectLinkWithParams.value = getSearchURL({
                baseURLLink: props.submitProjectLink,
                query: query.value,
                location: location.value,
                sourceComponent: 'navbar_search_expanded',
            });
        }

        if (event.target.value.length >= 2) {
            querySuggestions.value = await getQueryAutocomplete(event.target.value);
            isQuerySearchActive.value = true;
        } else {
            querySuggestions.value = [];
        }
    }

    function onQuerySuggestionClick(event: Event, querySuggestion: string) {
        event.preventDefault();
        query.value = querySuggestion;

        setTimeout(() => {
            isQuerySearchActive.value = false;
        }, 100);
    }

    function onQueryBlur() {
        isQuerySearchActive.value = false;
    }

    function clearQuery() {
        query.value = '';
        querySuggestions.value = [];
    }

    function computeSubmitProjectLinkWithParams() {
        if (props.submitProjectLink) {
            if (props.briefOrigin) {
                const url = props.submitProjectLink;
                if (!url.includes('brief_origin')) {
                    return `${url}${url.includes('?') ? '&' : '?'}brief_origin=${props.briefOrigin}`;
                }
            }

            return props.submitProjectLink;
        }
        return '';
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@navbar-unlogged/scss/variables.scss' as vars;

    $max-input-width: none;
    $min-input-width: 200px;

    .freelance-search {
        @include vars.flex-column-align-center;
        width: 100%;
        font-family: var(--joy-font-family-base);

        @include mq.screen_sm {
            @include vars.card-without-transition;
            & {
                padding: 19px 17px 29px 17px;
                align-items: flex-start;
                background-color: var(--joy-color-neutral-0);
                box-shadow: var(--joy-core-elevation-2);
            }
        }

        .search-card {
            @include vars.flex-row-center;
            @include vars.card-without-transition;
            & {
                background-color: var(--joy-color-neutral-0);
                gap: 16px;
                flex-wrap: wrap;
                padding: 32px;
                width: 100%;
            }

            @include mq.screen_sm {
                flex-direction: column;
                padding: 0;
                box-shadow: none;
            }

            .search-form {
                @include vars.flex-row-justify-center;
                align-items: baseline;
                flex-wrap: wrap;
                gap: 20px;
                width: 100%;

                @include mq.screen_sm {
                    flex-direction: column;
                    gap: 32px;
                }

                .joy-button {
                    @include mq.screen_sm {
                        width: 100%;
                    }
                }
            }

            .inputs {
                display: flex;
                gap: 20px;
                width: 100%;
                flex: 1 1 0;

                @include mq.screen_sm {
                    flex-direction: column;
                    gap: 16px;
                }

                .suggestions {
                    position: absolute;
                    z-index: 10;
                    top: 52px;
                    width: 100%;
                    background-color: var(--joy-color-neutral-0);
                    list-style: none;
                    border-radius: var(--joy-core-radius-4);
                    border: solid 2px var(--joy-color-secondary-30);
                    overflow: hidden;
                    display: none;
                    color: var(--joy-color-neutral-50);

                    &.visible {
                        display: block;
                    }

                    ul {
                        max-height: 185px;
                        overflow: auto;
                        padding: 0;
                        margin: 0;

                        li {
                            padding: 10px 15px;
                            font-size: var(--joy-font-size-primary-400);
                            line-height: 17px;

                            &:hover {
                                background-color: var(--joy-color-secondary-10);
                            }

                            display: flex;
                            align-items: baseline;
                            gap: 8px;
                        }

                        @include vars.nicer-scrollbar;
                    }
                }

                .clear-button {
                    position: absolute;
                    right: var(--joy-core-spacing-4);
                    top: var(--joy-core-spacing-4);
                    cursor: pointer;
                    background: transparent;
                    display: none;
                    border: none;

                    &.visible {
                        display: block;
                    }
                }

                .category {
                    display: flex;
                    flex-direction: column;
                    flex: 1 1 0;
                    min-width: 0;
                    max-width: $max-input-width;
                    position: relative;

                    @include mq.screen_sm {
                        max-width: none;
                        width: 100%;
                    }

                    .category-input {
                        width: 100%;
                        background-image: url('//dam.malt.com/navbar/icons/header/unlogged/search-gray.svg');
                        &:focus {
                            background-image: url('//dam.malt.com/navbar/icons/header/unlogged/search.svg');
                        }

                        @include mq.screen_sm {
                            max-width: none;
                            min-width: auto;
                        }
                    }

                    .category-error {
                        color: var(--joy-color-primary-30);
                        margin-top: 10px;
                    }
                }

                .location {
                    flex: 1 1 0;
                    min-width: 0;
                    max-width: $max-input-width;
                    position: relative;

                    @include mq.screen_sm {
                        max-width: none;
                        width: 100%;
                    }
                }
            }

            .links {
                display: flex;
                align-items: center;
                max-height: 48px;
                gap: 16px;

                @include mq.screen_sm {
                    @include vars.flex-column-align-center;
                    width: 100%;
                    gap: 30px;
                    max-height: none;
                }
            }

            .submit-project {
                display: flex;
                align-items: center;
                gap: 16px;
                a {
                    @include vars.link;
                }
            }

            .ai-search-cta {
                width: 100%;

                @include mq.screen_md(min) {
                    width: auto;
                }
            }

            input {
                @include vars.input;
                font-weight: var(--joy-font-weight-bold);
                line-height: var(--joy-line-height-large);

                &::placeholder {
                    font-weight: var(--joy-font-weight-normal);
                }

                &[type='text'] {
                    max-width: $max-input-width;
                    min-width: $min-input-width;
                    width: 100%;
                    height: 48px;
                    padding: 12px 45px;
                    background-repeat: no-repeat;
                    background-size: 21px auto;
                    background-position: 15px center;
                    color: var(--joy-color-neutral-50);
                }
            }

            .between-ctas {
                margin-right: 16px;
                color: var(--joy-color-neutral-50);
                font-size: var(--joy-font-size-primary-400);
                @include mq.screen_sm() {
                    display: none;
                }
            }
        }
        .cta-separator {
            display: none;
            @include mq.screen_md(min) {
                display: block;
                height: 48px;
                width: 1px;
                background: var(--joy-color-neutral-30);
            }
        }
    }
</style>
